import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import { CustomerMultiSelect } from '@/components/accounts-receivable/CustomerMultiSelect';
import { CaretDownIcon, CaretUpIcon, XIcon } from '@phosphor-icons/react';
import { ArReportSettings } from '@/components/accounts-receivable/ArReportSettings';
import {
  type ArReportFilters,
  type Preset,
  PRESETS,
} from '@/components/accounts-receivable/ar-report-filters';
import { ActiveFilterSummary, type CustomerOption } from './ar-report-shared';
import { T, useGT } from 'gt-react';

interface ArReportFilterPanelProps {
  settings: ArReportFilters;
  /** Show the basis as a read-only indicator (org locked the basis). */
  lockBasis?: boolean;
  activePreset: string | null;
  selectedCustomers: CustomerOption[];
  selectedStatuses: MultiSelectOption[];
  lastSentMinDays: number | undefined;
  lastSentMaxDays: number | undefined;
  includeNeverSent: boolean;
  filterPanelOpen: boolean;
  onFilterPanelOpenChange: (open: boolean) => void;
  onPresetClick: (preset: Preset) => void;
  onSettingsChange: (value: ArReportFilters) => void;
  onCustomersChange: (customers: CustomerOption[]) => void;
  onStatusesChange: (statuses: MultiSelectOption[]) => void;
  onLastSentMinDaysChange: (value: number | undefined) => void;
  onLastSentMaxDaysChange: (value: number | undefined) => void;
  onIncludeNeverSentChange: (value: boolean) => void;
}

export function ArReportFilterPanel({
  settings,
  lockBasis = false,
  activePreset,
  selectedCustomers,
  selectedStatuses,
  lastSentMinDays,
  lastSentMaxDays,
  includeNeverSent,
  filterPanelOpen,
  onFilterPanelOpenChange,
  onPresetClick,
  onSettingsChange,
  onCustomersChange,
  onStatusesChange,
  onLastSentMinDaysChange,
  onLastSentMaxDaysChange,
  onIncludeNeverSentChange,
}: ArReportFilterPanelProps) {
  function parseDayInput(raw: string): number | undefined {
    const trimmed = raw.trim();
    if (trimmed === '') return undefined;
    const val = parseInt(trimmed, 10);
    if (Number.isNaN(val)) return undefined;
    return Math.max(0, val);
  }
  const hasLastSentFilter =
    lastSentMinDays !== undefined ||
    lastSentMaxDays !== undefined ||
    includeNeverSent;
  const gt = useGT();
  return (
    <Collapsible open={filterPanelOpen} onOpenChange={onFilterPanelOpenChange}>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              <T context="filter panel section title">Filters</T>
            </CardTitle>
            <div className="flex items-center gap-3">
              {!filterPanelOpen && (
                <ActiveFilterSummary
                  settings={settings}
                  activePreset={activePreset}
                  selectedCustomers={selectedCustomers}
                  selectedStatuses={selectedStatuses}
                  lastSentMinDays={lastSentMinDays}
                  lastSentMaxDays={lastSentMaxDays}
                  includeNeverSent={includeNeverSent}
                />
              )}
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  {filterPanelOpen ? (
                    <CaretUpIcon className="h-4 w-4" />
                  ) : (
                    <CaretDownIcon className="h-4 w-4" />
                  )}
                  <span className="sr-only">
                    <T context="screen reader label for collapsible filter panel toggle button">
                      Toggle filters
                    </T>
                  </span>
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {/* Row 1: Day Range Presets */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground w-20 shrink-0">
                <T context="filter panel presets label">Presets</T>
              </span>
              <div className="flex flex-wrap gap-1">
                {PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    variant={
                      activePreset === preset.label ? 'default' : 'outline'
                    }
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    onClick={() => onPresetClick(preset)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Row 2: Basis + Day Range via shared component */}
            <ArReportSettings
              value={settings}
              onChange={onSettingsChange}
              label=""
              lockBasis={lockBasis}
            />

            {/* Row 2: Customer filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground w-20 shrink-0">
                <T context="filter panel customers label">Customers</T>
              </span>
              <CustomerMultiSelect
                value={selectedCustomers}
                onChange={onCustomersChange}
                className="h-7 min-w-[180px]"
              />
              {selectedCustomers.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  title={gt('Clear customer filter', {
                    context:
                      'button tooltip to remove all selected customer filters',
                  })}
                  onClick={() => onCustomersChange([])}
                >
                  <XIcon className="h-3.5 w-3.5" />
                  <span className="sr-only">
                    <T context="screen reader label for clear customer filter button">
                      Clear customer filter
                    </T>
                  </span>
                </Button>
              )}
            </div>

            {/* Row 3: Status filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground w-20 shrink-0">
                <T context="filter panel status label">Status</T>
              </span>
              <MultiSelect
                options={[
                  {
                    id: 'draft',
                    label: gt('Draft', {
                      context: 'invoice status filter option',
                    }),
                  },
                  {
                    id: 'open',
                    label: gt('Open', {
                      context: 'invoice status filter option',
                    }),
                  },
                  {
                    id: 'closed',
                    label: gt('Closed', {
                      context: 'invoice status filter option',
                    }),
                  },
                  {
                    id: 'voided',
                    label: gt('Voided', {
                      context: 'invoice status filter option',
                    }),
                  },
                ]}
                value={selectedStatuses}
                onChange={onStatusesChange}
                placeholder={gt('All statuses', {
                  context: 'placeholder for status multi-select filter',
                })}
                size="sm"
                shouldAutoSort={false}
                className="h-7 min-w-[160px]"
              />
              {selectedStatuses.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  title={gt('Clear status filter', {
                    context:
                      'button tooltip to remove all selected status filters',
                  })}
                  onClick={() => onStatusesChange([])}
                >
                  <XIcon className="h-3.5 w-3.5" />
                  <span className="sr-only">
                    <T context="screen reader label for clear status filter button">
                      Clear status filter
                    </T>
                  </span>
                </Button>
              )}
            </div>

            {/* Row 4: Last statement sent filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground w-20 shrink-0">
                <T context="filter panel last statement sent label">
                  Last sent
                </T>
              </span>
              <div className="flex items-center gap-1">
                <Label
                  htmlFor="ar-last-sent-min"
                  className="text-xs text-muted-foreground"
                >
                  <T context="filter input label - customers who haven't received a statement in N+ days">
                    Not sent in
                  </T>
                </Label>
                <Input
                  id="ar-last-sent-min"
                  type="number"
                  min={0}
                  inputMode="numeric"
                  className="h-7 w-20"
                  value={lastSentMinDays ?? ''}
                  onChange={(e) =>
                    onLastSentMinDaysChange(parseDayInput(e.target.value))
                  }
                  placeholder={gt('Any', {
                    context:
                      'placeholder for the not-sent-in days filter input when no value is set',
                  })}
                />
                <span className="text-xs text-muted-foreground">
                  <T context="suffix after the not-sent-in days input">days</T>
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Label
                  htmlFor="ar-last-sent-max"
                  className="text-xs text-muted-foreground"
                >
                  <T context="filter input label - customers whose last statement was within the past N days">
                    Sent within
                  </T>
                </Label>
                <Input
                  id="ar-last-sent-max"
                  type="number"
                  min={0}
                  inputMode="numeric"
                  className="h-7 w-20"
                  value={lastSentMaxDays ?? ''}
                  onChange={(e) =>
                    onLastSentMaxDaysChange(parseDayInput(e.target.value))
                  }
                  placeholder={gt('Any', {
                    context:
                      'placeholder for the sent-within days filter input when no value is set',
                  })}
                />
                <span className="text-xs text-muted-foreground">
                  <T context="suffix after the sent-within days input">days</T>
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Checkbox
                  id="ar-include-never-sent"
                  checked={includeNeverSent}
                  onCheckedChange={(checked) =>
                    onIncludeNeverSentChange(checked === true)
                  }
                />
                <Label
                  htmlFor="ar-include-never-sent"
                  className="text-xs cursor-pointer"
                >
                  <T context="checkbox label include customers who have never received a statement">
                    Include never-sent customers
                  </T>
                </Label>
              </div>
              {hasLastSentFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  title={gt('Clear last-sent filter', {
                    context:
                      'button tooltip to remove the last-statement-sent filter',
                  })}
                  onClick={() => {
                    onLastSentMinDaysChange(undefined);
                    onLastSentMaxDaysChange(undefined);
                    onIncludeNeverSentChange(false);
                  }}
                >
                  <XIcon className="h-3.5 w-3.5" />
                  <span className="sr-only">
                    <T context="screen reader label for clear last-sent filter button">
                      Clear last-sent filter
                    </T>
                  </span>
                </Button>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
